import React, { useCallback, useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import { ConversationData, MessageType } from "../interfaces/all";
import { getUnreadMessagesCount } from "../services/api";
import { normalizePhoneNumber } from "../utils/phone-utils";

interface UseConversationReadStatusProps {
  socket: React.RefObject<Socket | null>;
  conversations: ConversationData[];
}

interface ConversationReadStatus {
  [phoneNumber: string]: number;
}

export const useConversationReadStatus = ({
  socket,
  conversations,
}: UseConversationReadStatusProps) => {
  const [readStatus, setReadStatus] = useState<ConversationReadStatus>({});

  // Refs para otimização
  const isProcessingRef = useRef(false);
  const lastConversationsRef = useRef<ConversationData[]>([]);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef<Map<string, number>>(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateConversationReadStatus = useCallback(
    async (phoneNumber: string) => {
      if (isProcessingRef.current) {
        return;
      }

      // Normalizar o número de telefone para garantir consistência
      const normalizedPhone = normalizePhoneNumber(phoneNumber);

      isProcessingRef.current = true;

      try {
        // Chamar a API real para obter o contador de mensagens não lidas
        const response = await getUnreadMessagesCount(normalizedPhone);

        if (response.status === "success") {
          const serverValue =
            typeof response.data === "number" ? response.data : 0;

          setReadStatus((prev) => {
            const currentValue = prev[normalizedPhone] || 0;

            // Só atualizar se o valor realmente mudou
            if (currentValue !== serverValue) {
              return {
                ...prev,
                [normalizedPhone]: serverValue,
              };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error("Erro ao atualizar status de leitura:", error);
        // Em caso de erro, manter o valor atual
      } finally {
        isProcessingRef.current = false;
      }
    },
    [normalizePhoneNumber],
  );

  // Função otimizada para atualizar o status de leitura para todas as conversas
  const updateAllConversationsReadStatus = useCallback(async () => {
    // Evitar processamento simultâneo
    if (isProcessingRef.current) {
      return;
    }

    // Verificar se as conversas realmente mudaram
    const currentConversations = conversations
      .map((conv) =>
        normalizePhoneNumber(conv.valid_telephone || conv.telephone),
      )
      .sort();
    const lastConversations = lastConversationsRef.current
      .map((conv) =>
        normalizePhoneNumber(conv.valid_telephone || conv.telephone),
      )
      .sort();

    if (
      JSON.stringify(currentConversations) === JSON.stringify(lastConversations)
    ) {
      return; // Não há mudanças, não precisa atualizar
    }

    isProcessingRef.current = true;

    try {
      // Atualizar conversas em paralelo com limite de concorrência
      const phoneNumbers = conversations
        .map((conv) =>
          normalizePhoneNumber(conv.valid_telephone || conv.telephone),
        )
        .filter(Boolean);
      const uniquePhoneNumbers = [...new Set(phoneNumbers)];

      // Processar em lotes de 3 para evitar sobrecarga
      const batchSize = 3;
      for (let i = 0; i < uniquePhoneNumbers.length; i += batchSize) {
        const batch = uniquePhoneNumbers.slice(i, i + batchSize);
        await Promise.all(
          batch.map((phone) => updateConversationReadStatus(phone)),
        );

        // Pequena pausa entre lotes
        if (i + batchSize < uniquePhoneNumbers.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Atualizar referência das últimas conversas processadas
      lastConversationsRef.current = conversations;
    } finally {
      isProcessingRef.current = false;
    }
  }, [conversations, updateConversationReadStatus, normalizePhoneNumber]);

  // useEffect para atualizar status de leitura quando as conversas mudam (com debounce)
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      updateAllConversationsReadStatus();
    }, 300); // Reduzido para 300ms para melhor responsividade

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [conversations, updateAllConversationsReadStatus]);

  // Efeito para configurar os listeners de socket
  useEffect(() => {
    const socketInstance = socket?.current;
    if (!socketInstance) return;

    const handleMessageReadStatusUpdate = (data: {
      messageId: string;
      to: string;
      isRead: boolean;
    }) => {
      // Normalizar o número de telefone para garantir consistência
      const normalizedPhone = normalizePhoneNumber(data.to);
      // Atualizar apenas a conversa específica
      updateConversationReadStatus(normalizedPhone);
    };

    const handleAllMessagesRead = (data: {
      phoneNumber: string;
      count: number;
    }) => {
      // Normalizar o número de telefone para garantir consistência
      const normalizedPhone = normalizePhoneNumber(data.phoneNumber);

      // Verificar se este número de telefone existe nas conversas atuais
      const conversationExists = conversations.some((conv) => {
        const convPhone = normalizePhoneNumber(
          conv.valid_telephone || conv.telephone,
        );
        return convPhone === normalizedPhone;
      });

      // Só atualizar se a conversa existe nas conversas atuais
      if (conversationExists) {
        // Atualizar imediatamente o contador para 0
        setReadStatus((prev) => {
          const newStatus = {
            ...prev,
            [normalizedPhone]: 0,
          };
          return newStatus;
        });

        // Limpar cache para forçar atualização
        cacheRef.current.clear();
      }
    };

    // Listener para novas mensagens que atualiza o contador
    const handleNewMessage = (data: MessageType & { companyId: string }) => {
      const phoneNumber = data.from || data.to;
      if (phoneNumber) {
        const normalizedPhone = normalizePhoneNumber(phoneNumber);

        // Incrementar o contador localmente apenas se a mensagem for do cliente
        if (data.sentBy === "client") {
          setReadStatus((prev) => {
            const currentCount = prev[normalizedPhone] || 0;
            const newCount = currentCount + 1;
            return {
              ...prev,
              [normalizedPhone]: newCount,
            };
          });
        }

        // Depois sincronizar com o servidor para garantir precisão
        setTimeout(() => {
          updateConversationReadStatus(normalizedPhone);
        }, 500); // Aumentado para dar mais tempo da mensagem ser salva no banco
      }
    };

    // Listener para batch de mensagens
    const handleNewMessageBatch = (
      data: MessageType[] | Record<string, MessageType>,
    ) => {
      // Converter para array se for objeto com índices numéricos
      const messagesArray = Array.isArray(data) ? data : Object.values(data);

      if (messagesArray.length > 0) {
        // Atualizar contadores localmente imediatamente apenas para mensagens do cliente
        setReadStatus((prev) => {
          const newStatus = { ...prev };
          let hasUpdates = false;

          messagesArray.forEach((message) => {
            if (message.sentBy === "client") {
              const phoneNumber = message.from || message.to;
              if (phoneNumber) {
                const normalizedPhone = normalizePhoneNumber(phoneNumber);
                const currentCount = newStatus[normalizedPhone] || 0;
                const newCount = currentCount + 1;
                newStatus[normalizedPhone] = newCount;
                hasUpdates = true;
              }
            }
          });
          return hasUpdates ? newStatus : prev;
        });

        // Depois sincronizar com o servidor
        setTimeout(() => {
          messagesArray.forEach((message) => {
            const phoneNumber = message.from || message.to;
            if (phoneNumber) {
              updateConversationReadStatus(phoneNumber);
            }
          });
        }, 200);
      }
    };

    socketInstance.on("messageReadStatusUpdate", handleMessageReadStatusUpdate);
    socketInstance.on("allMessagesRead", handleAllMessagesRead);
    socketInstance.on("newMessage", handleNewMessage);
    socketInstance.on("newMessageBatch", handleNewMessageBatch);

    return () => {
      socketInstance.off(
        "messageReadStatusUpdate",
        handleMessageReadStatusUpdate,
      );
      socketInstance.off("allMessagesRead", handleAllMessagesRead);
      socketInstance.off("newMessage", handleNewMessage);
      socketInstance.off("newMessageBatch", handleNewMessageBatch);
    };
  }, [socket, updateConversationReadStatus, normalizePhoneNumber]);

  // Efeito para monitorar mudanças no estado readStatus
  useEffect(() => {
    // Limpar cache quando readStatus muda
    cacheRef.current.clear();
  }, [readStatus]);

  // Função otimizada para obter contador de mensagens não lidas com cache
  const getUnreadCount = useCallback(
    (conversation: ConversationData) => {
      const phoneNumber =
        conversation.valid_telephone || conversation.telephone;
      const normalizedPhone = normalizePhoneNumber(phoneNumber);

      // Verificar cache primeiro
      const cacheKey = `${conversation.id}-${normalizedPhone}`;
      const cachedCount = cacheRef.current.get(cacheKey);
      const currentCount = readStatus[normalizedPhone] || 0;

      // Se o valor mudou, atualizar cache
      if (cachedCount !== currentCount) {
        cacheRef.current.set(cacheKey, currentCount);
      }

      return currentCount;
    },
    [readStatus, normalizePhoneNumber],
  );

  // Cleanup adequado para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      // Limpar timeouts
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      // Limpar refs para liberar memória
      isProcessingRef.current = false;
      lastConversationsRef.current = [];
      cacheRef.current.clear();
    };
  }, []);

  return {
    getUnreadCount,
    updateConversationReadStatus,
  };
};
